import { z } from 'zod';

/**
 * One schema for both surfaces, because there is one dialog.
 *
 * The tag form simply never renders a parent control, which is the same rule the
 * markup follows: the field is **absent** on that page rather than present and
 * dead, and the dialog reads its absence rather than branching on which page it
 * is standing on.
 *
 * **The messages are injected rather than written here.** Every user-facing
 * string this repo renders lives in the owning feature's `copy.ts`, and a zod
 * message is a user-facing string — a literal in this file would be the one
 * Arabic sentence in the feature that a copy review never sees.
 *
 * `160` is the column width on both `content__categories.name` and
 * `content__tags.name`. It is a schema fact rather than a configured value, so
 * it is stated here: the alternative is letting the operator type two hundred
 * characters and meeting a `422` for a bound the field could have shown them.
 */

export const TERM_NAME_MAX_LENGTH = 160;

/** The `<select>` value that means «no parent». Folded to `null` on submit. */
export const ROOT_PARENT_VALUE = '';

export interface TermFormMessages {
  nameRequired: string;
  nameTooLong: string;
}

export function termFormSchema(messages: TermFormMessages) {
  return z.object({
    name: z.string().trim().min(1, messages.nameRequired).max(TERM_NAME_MAX_LENGTH, messages.nameTooLong),
    /**
     * Kept a plain string so the resolver's input and output types stay
     * identical — a `transform` here makes the form's value type disagree with
     * the field's, and the fold is one line at the submit boundary instead.
     */
    parentId: z.string(),
  });
}

export type TermFormValues = z.infer<ReturnType<typeof termFormSchema>>;

/** `''` is «root», and `null` is what the request body carries for it. */
export function toParentId(value: string): string | null {
  return value === ROOT_PARENT_VALUE ? null : value;
}
